/**
 * Behaviour shared by dialog, alert-dialog, sheet and drawer.
 *
 * Radix's Dialog is one pattern with four skins: shadcn's Sheet is a Dialog
 * with a `side`, AlertDialog is a Dialog with `role=alertdialog`, no outside
 * dismissal and Cancel focused first, and vaul's Drawer is a Dialog that can
 * also be dragged. This module owns the pattern once, parameterised by the slot
 * prefix; the four public primitives are thin wrappers.
 *
 * Lifecycle of one open/close:
 *
 * 1. open  -> `hidden` off, `data-state="open"`, `mountLayer` (portal, trap,
 *    scroll lock, inert beneath, initial focus). The overlay rides in the same
 *    portal wrapper, directly before the content.
 * 2. close -> `data-state="closed"` everywhere, then `exitWhenDone` waits for
 *    the exit animation, then the layer is destroyed (content and overlay go
 *    back to their slots, `hidden` is restored, focus returns to the opener).
 * 3. reopen while exiting -> the pending teardown is cancelled; the layer is
 *    still mounted so only the state flips back.
 */
import { generateId } from 'hydrogen/id';
import { partsOf, partTarget, setState } from './_dom.js';
import { mountLayer } from './_layer.js';
import { exitWhenDone } from './_presence.js';
const noop = () => { };
/** The first element with `slot` inside `content` that `content` (not a nested dialog) owns. */
function inContent(content, contentSlot, slot) {
    for (const candidate of content.querySelectorAll(`[data-slot="${slot}"]`)) {
        if (candidate.closest(`[data-slot="${contentSlot}"]`) === content)
            return candidate;
    }
    return null;
}
export function createDialog(root, options, config) {
    const { name } = config;
    const contentSlot = `${name}-content`;
    const triggers = partsOf(root, name, `${name}-trigger`);
    const content = partsOf(root, name, contentSlot)[0] ?? null;
    const overlay = partsOf(root, name, `${name}-overlay`)[0] ?? null;
    const controlled = options.open !== undefined;
    const modal = options.modal ?? true;
    const closeSelector = [`${name}-close`, ...(config.closeSlots ?? [])]
        .map((slot) => `[data-slot="${slot}"]`)
        .join(',');
    let current = controlled ? options.open === true : options.defaultOpen === true;
    let layer = null;
    let cancelExit = noop;
    let overlayMarker = null;
    let lastTrigger = null;
    let destroyed = false;
    const cleanups = [];
    if (content)
        content.id ||= `${name}-content-${generateId()}`;
    const contentId = content?.id ?? '';
    const parts = content ? { root, content, overlay } : null;
    // --- static wiring -------------------------------------------------------
    for (const trigger of triggers) {
        const target = partTarget(trigger);
        target.setAttribute('aria-haspopup', 'dialog');
        if (content)
            target.setAttribute('aria-controls', contentId);
        if (target instanceof HTMLButtonElement && !target.hasAttribute('type')) {
            target.setAttribute('type', 'button');
        }
        const onClick = () => {
            if (!current)
                lastTrigger = target;
            request(!current);
        };
        target.addEventListener('click', onClick);
        cleanups.push(() => target.removeEventListener('click', onClick));
    }
    if (content) {
        content.setAttribute('role', config.role);
        if (modal)
            content.setAttribute('aria-modal', 'true');
        else
            content.removeAttribute('aria-modal');
        const onContentClick = (event) => {
            if (!(event.target instanceof Element))
                return;
            const closer = event.target.closest(closeSelector);
            if (closer && closer.closest(`[data-slot="${contentSlot}"]`) === content)
                request(false);
        };
        content.addEventListener('click', onContentClick);
        cleanups.push(() => content.removeEventListener('click', onContentClick));
    }
    function wireLabels(target) {
        const title = inContent(target, contentSlot, `${name}-title`);
        const description = inContent(target, contentSlot, `${name}-description`);
        if (title && !target.hasAttribute('aria-label')) {
            title.id ||= `${name}-title-${generateId()}`;
            target.setAttribute('aria-labelledby', title.id);
        }
        if (description) {
            description.id ||= `${name}-description-${generateId()}`;
            target.setAttribute('aria-describedby', description.id);
        }
    }
    // --- state ---------------------------------------------------------------
    function paint(state) {
        setState(state, root, content, overlay);
        for (const trigger of triggers) {
            const target = partTarget(trigger);
            setState(state, trigger, target);
            target.setAttribute('aria-expanded', String(state === 'open'));
        }
    }
    function initialFocus() {
        if (config.initialFocusSlot && content) {
            const preferred = inContent(content, contentSlot, config.initialFocusSlot);
            if (preferred)
                return partTarget(preferred);
        }
        return 'first';
    }
    function mount() {
        if (!content || layer)
            return;
        // Safari does not focus a button on click, so the trigger that opened us is the opener.
        const active = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        const opener = lastTrigger ?? active;
        lastTrigger = null;
        layer = mountLayer({
            content,
            modal,
            trapFocus: modal,
            initialFocus: initialFocus(),
            returnFocusTo: opener,
            dismissOnEscape: options.closeOnEscape ?? true,
            dismissOnOutsidePointer: options.closeOnOutsideClick ?? true,
            isInside: (node) => triggers.some((trigger) => trigger.contains(node)),
            onDismiss: () => request(false),
        });
        if (overlay && content.parentElement) {
            overlayMarker = overlay.parentNode ? document.createComment(`${name}-overlay`) : null;
            if (overlayMarker && overlay.parentNode)
                overlay.parentNode.insertBefore(overlayMarker, overlay);
            content.parentElement.insertBefore(overlay, content);
        }
    }
    function unmount() {
        cancelExit = noop;
        const mounted = layer;
        layer = null;
        if (overlay) {
            if (overlayMarker?.parentNode) {
                overlayMarker.parentNode.insertBefore(overlay, overlayMarker);
                overlayMarker.remove();
            }
            else {
                overlay.remove();
            }
            overlayMarker = null;
            overlay.hidden = true;
        }
        if (content)
            content.hidden = true;
        mounted?.destroy();
    }
    function show() {
        if (!content)
            return;
        cancelExit();
        cancelExit = noop;
        content.hidden = false;
        if (overlay)
            overlay.hidden = false;
        wireLabels(content);
        if (parts)
            config.onStateChange?.(true, parts);
        paint('open');
        mount();
    }
    function hide() {
        paint('closed');
        if (parts)
            config.onStateChange?.(false, parts);
        if (!layer || !content)
            return;
        cancelExit();
        let pending = overlay ? 2 : 1;
        const done = () => {
            pending -= 1;
            if (pending === 0)
                unmount();
        };
        const cancels = [exitWhenDone(content, done)];
        if (overlay)
            cancels.push(exitWhenDone(overlay, done));
        cancelExit = () => {
            for (const cancel of cancels)
                cancel();
        };
    }
    function apply(next) {
        if (destroyed || next === current)
            return;
        current = next;
        if (next)
            show();
        else
            hide();
    }
    function request(next) {
        if (destroyed || next === current)
            return;
        options.onOpenChange?.(next);
        if (!controlled)
            apply(next);
    }
    // --- initial paint ---------------------------------------------------------
    if (content)
        content.hidden = true;
    if (overlay)
        overlay.hidden = true;
    paint('closed');
    if (current) {
        current = false;
        apply(true);
    }
    return {
        open: () => request(true),
        close: () => request(false),
        toggle: () => request(!current),
        setOpen: apply,
        get isOpen() {
            return current;
        },
        get parts() {
            return parts;
        },
        destroy() {
            if (destroyed)
                return;
            destroyed = true;
            cancelExit();
            cancelExit = noop;
            for (const cleanup of cleanups.splice(0))
                cleanup();
            current = false;
            paint('closed');
            unmount();
        },
    };
}
